import axios from 'axios';
import fileDownload from 'js-file-download';

// Derive a human-readable file name from a URL's last path segment.
export const getFileNameFromUrl = (url: string) => {
  const lastSegment = url.split('/').pop() || url;
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    // lastSegment may contain a bare "%" (e.g. "50%off.pdf") that isn't a valid
    // percent-encoding, which makes decodeURIComponent throw; keep it as-is
    return lastSegment;
  }
};

// Download the file at `url` as a blob so the browser saves it instead of
// navigating to it. Falls back to opening the file in a new tab when a blob
// download isn't permitted (e.g. the storage host disallows the CORS request).
export const downloadFromUrl = async (url: string, fileName?: string) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    fileDownload(response.data, fileName ?? getFileNameFromUrl(url));
  } catch {
    window.open(url, '_blank', 'noopener');
  }
};

// Quote a CSV field: wrap in double quotes and double any embedded quote, so
// commas, quotes and newlines inside the value can't break the column layout.
const escapeCsvField = (value: string | number | undefined | null): string => {
  const text = value === undefined || value === null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

// Build a CSV string and hand it to the browser as a download.
//
// The BOM is required: Excel on Windows reads a BOM-less UTF-8 CSV as ANSI and
// renders Chinese as mojibake.
export const downloadCsv = (
  fileName: string,
  headers: string[],
  rows: (string | number | undefined | null)[][]
) => {
  const BOM = '\uFEFF';
  const content = [headers, ...rows]
    .map((row) => row.map(escapeCsvField).join(','))
    .join('\r\n');
  fileDownload(
    new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' }),
    fileName
  );
};
